import { createTheme, alpha } from '@mui/material/styles';

const colors = {
  primary: '#1A73E8',
  primaryLight: '#4285F4',
  primaryDark: '#1557B0',
  success: '#1E8E3E',
  warning: '#F9AB00',
  error: '#D93025',
  info: '#1A73E8',
  bg: '#F8F9FA',
  paper: '#FFFFFF',
  textPrimary: '#202124',
  textSecondary: '#5F6368',
  border: '#DADCE0',
  grey50: '#F8F9FA',
  grey100: '#F1F3F4',
  grey200: '#E8EAED',
  grey300: '#DADCE0',
  grey400: '#BDC1C6',
  grey900: '#202124',
};

const theme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      light: colors.primaryLight,
      dark: colors.primaryDark,
      contrastText: '#fff',
    },
    secondary: {
      main: '#5F6368',
      light: '#80868B',
      dark: '#3C4043',
    },
    success: { main: colors.success, light: '#E6F4EA', dark: '#137333' },
    warning: { main: colors.warning, light: '#FEF7E0', dark: '#E37400' },
    error: { main: colors.error, light: '#FCE8E6', dark: '#B31412' },
    info: { main: colors.info, light: '#E8F0FE', dark: '#1557B0' },
    background: {
      default: colors.bg,
      paper: colors.paper,
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
      disabled: colors.grey400,
    },
    divider: colors.border,
    grey: {
      50: colors.grey50,
      100: colors.grey100,
      200: colors.grey200,
      300: colors.grey300,
      400: colors.grey400,
      500: '#9AA0A6',
      600: '#80868B',
      700: '#5F6368',
      800: '#3C4043',
      900: colors.grey900,
    },
  },
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
    '0 1px 2px 0 rgba(60,64,67,0.3), 0 2px 6px 2px rgba(60,64,67,0.15)',
    '0 1px 3px 0 rgba(60,64,67,0.3), 0 4px 8px 3px rgba(60,64,67,0.15)',
    '0 2px 3px 0 rgba(60,64,67,0.3), 0 6px 10px 4px rgba(60,64,67,0.15)',
    '0 4px 4px 0 rgba(60,64,67,0.3), 0 8px 12px 6px rgba(60,64,67,0.15)',
    '0 4px 4px 0 rgba(60,64,67,0.3), 0 8px 12px 6px rgba(60,64,67,0.15)',
    ...Array(18).fill('0 4px 4px 0 rgba(60,64,67,0.3), 0 8px 12px 6px rgba(60,64,67,0.15)'),
  ],
  typography: {
    fontFamily: '"Google Sans", "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    h1: { fontWeight: 400, fontSize: '2rem', lineHeight: 1.25, color: colors.textPrimary },
    h2: { fontWeight: 400, fontSize: '1.75rem', lineHeight: 1.3, color: colors.textPrimary },
    h3: { fontWeight: 500, fontSize: '1.375rem', lineHeight: 1.35, color: colors.textPrimary },
    h4: { fontWeight: 500, fontSize: '1.125rem', lineHeight: 1.4, color: colors.textPrimary },
    h5: { fontWeight: 500, fontSize: '1rem', lineHeight: 1.45 },
    h6: { fontWeight: 500, fontSize: '0.875rem', lineHeight: 1.5 },
    subtitle1: { fontWeight: 500, fontSize: '0.875rem', color: colors.textSecondary },
    subtitle2: { fontWeight: 500, fontSize: '0.8125rem', color: colors.textSecondary },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.55 },
    caption: { fontSize: '0.75rem', lineHeight: 1.5, color: colors.textSecondary },
    button: { fontWeight: 500, fontSize: '0.875rem', letterSpacing: '0.01em' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '*': { scrollbarWidth: 'thin', scrollbarColor: `${colors.grey300} transparent` },
        '*::-webkit-scrollbar': { width: 6, height: 6 },
        '*::-webkit-scrollbar-thumb': { backgroundColor: colors.grey300, borderRadius: 3 },
        '*::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        body: {
          backgroundColor: colors.bg,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
          boxShadow: 'none',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
          },
        },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 8,
          border: `1px solid ${colors.border}`,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 4,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          padding: '8px 24px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
          },
        },
        outlined: {
          borderColor: colors.border,
          color: colors.primary,
          '&:hover': { backgroundColor: alpha(colors.primary, 0.04), borderColor: colors.primary },
        },
        sizeSmall: { padding: '4px 16px', fontSize: '0.8125rem' },
        sizeLarge: { padding: '12px 32px', fontSize: '0.9375rem' },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: '50%',
          '&:hover': { backgroundColor: alpha(colors.textPrimary, 0.04) },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 16, fontWeight: 500, fontSize: '0.8125rem' },
        sizeSmall: { height: 24, fontSize: '0.75rem' },
      },
    },
    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'small' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            backgroundColor: '#fff',
            '& fieldset': { borderColor: colors.grey300 },
            '&:hover fieldset': { borderColor: colors.textSecondary },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          '& fieldset': { borderColor: colors.grey300 },
          '&:hover fieldset': { borderColor: colors.textSecondary },
          '&.Mui-focused fieldset': { borderWidth: 2 },
        },
      },
    },
    MuiSelect: {
      styleOverrides: { root: { borderRadius: 4 } },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: colors.grey50,
            color: colors.textSecondary,
            fontWeight: 500,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            borderBottom: `1px solid ${colors.border}`,
            padding: '12px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': { backgroundColor: alpha(colors.primary, 0.04) },
          },
          '& .MuiTableCell-root': {
            borderBottom: `1px solid ${colors.border}`,
            padding: '12px 16px',
            fontSize: '0.8125rem',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          minHeight: 48,
          color: colors.textSecondary,
          '&.Mui-selected': { color: colors.primary },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 3, borderRadius: '3px 3px 0 0' },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: '0 4px 4px 0 rgba(60,64,67,0.3), 0 8px 12px 6px rgba(60,64,67,0.15)',
          border: 'none',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 4, fontSize: '0.8125rem' },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: colors.grey900,
          fontSize: '0.75rem',
          borderRadius: 4,
          padding: '6px 12px',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: { root: { fontSize: '0.875rem', fontWeight: 500 } },
    },
    MuiLinearProgress: {
      styleOverrides: { root: { borderRadius: 4, height: 4 } },
    },
    MuiDrawer: {
      styleOverrides: { paper: { border: 'none' } },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: { borderRadius: 4 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 4,
          boxShadow: '0 2px 3px 0 rgba(60,64,67,0.3), 0 6px 10px 4px rgba(60,64,67,0.15)',
          border: `1px solid ${colors.border}`,
        },
      },
    },
    MuiDivider: {
      styleOverrides: { root: { borderColor: colors.border } },
    },
  },
});

export default theme;
